import moment from "moment";
import { ApiMediaURL } from "../../../setting/GlobleVariable";

/** Normalize DB shape: `event` may be `[doc]` or a single embedded object. */
export function getInnerEventFromWrapper(wrapper) {
  const e = wrapper?.event;
  if (Array.isArray(e) && e.length) return e[0];
  if (e && typeof e === "object") return e;
  return null;
}

function eventImageUrl(wrapper, ev) {
  if (wrapper?.image) return `${ApiMediaURL}/${wrapper.image}`;
  if (ev?.cover) return `${ApiMediaURL}/${ev.cover}`;
  if (ev?.eventImage) return `${ApiMediaURL}/${ev.eventImage}`;
  return "";
}

function ticketPriceNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Listing price shown on the ticket page sidebar (TEVO: lowest retail after PriceAsc;
 * user-created: first ticket row).
 */
export function resolveListingPrice(ev, wrapper) {
  if (!ev) {
    const w = ticketPriceNumber(wrapper?.ticketPrice);
    return w ?? 0;
  }

  if (Array.isArray(ev.userTickets) && ev.userTickets.length > 0) {
    const first = ev.userTickets[0];
    const fromFirst = ticketPriceNumber(
      first?.price ?? first?.retail_price ?? first?.tgPrice
    );
    if (fromFirst != null && fromFirst > 0) return fromFirst;
  }

  if (Array.isArray(ev.products) && ev.products.length > 0) {
    const productPrices = ev.products
      .map((p) => ticketPriceNumber(p?.price ?? p?.retail_price))
      .filter((n) => n != null && n > 0);
    if (productPrices.length) return Math.min(...productPrices);
  }

  const hints = [
    ev?.min_price,
    ev?.lowest_price,
    ev?.ticket_min_price,
    ev?.price,
    ev?.ticketPrice,
    wrapper?.ticketPrice,
  ];
  for (const h of hints) {
    const p = ticketPriceNumber(h);
    if (p != null && p > 0) return p;
  }

  return 0;
}

/** Lowest retail in TEVO ticket_groups (matches ticket page default PriceAsc sort). */
export function lowestPriceFromTicketGroups(groups) {
  if (!Array.isArray(groups) || groups.length === 0) return 0;
  const prices = groups
    .map((g) =>
      ticketPriceNumber(g?.retail_price ?? g?.price ?? g?.wholesale_price)
    )
    .filter((n) => n != null && n > 0);
  return prices.length ? Math.min(...prices) : 0;
}

export function isTevoNumericEventId(id) {
  if (id == null || id === "") return false;
  const str = String(id);
  if (/^[a-f\d]{24}$/i.test(str)) return false;
  const n = Number(str);
  return Number.isFinite(n) && n > 0;
}

function venueLabel(ev) {
  const v = ev?.venue;
  if (v && typeof v === "object") return v.name || v.city || "";
  return v || "";
}

/**
 * Admin "Latest Events" (isFeatured) for the discover Hot Events grid — same source
 * as the Latest Events dashboard tab, without the TEVO ticket-count gate so
 * user-created / inventory-backed rows still appear.
 */
export function selectLatestFeaturedForHot(featuredEvents) {
  return (
    featuredEvents?.filter((item) => {
      if (item?.isFeatured !== true) return false;
      if (item?.isCommunity === true) return false;
      return !!getInnerEventFromWrapper(item);
    }) || []
  );
}

/** An event an organizer made on InstaPass (it has its own ticket types). */
export function isOrganizerEvent(item) {
  const ev = item?.event?.[0];
  return Array.isArray(ev?.userTickets) && ev.userTickets.length > 0;
}

/** Non-featured marketplace copies only; organizer events go to Community. */
export function selectTrendingFeatured(featuredEvents) {
  return (
    featuredEvents?.filter(
      (item) =>
        item?.isFeatured === false &&
        !item?.event?.[0]?._isUserCreated &&
        !isOrganizerEvent(item) &&
        item?.isCommunity !== true
    ) || []
  );
}

/** Admin-picked community events plus every public organizer-made event, soonest first. */
export function selectCommunityItems(featuredEvents) {
  const list =
    featuredEvents?.filter(
      (item) =>
        item?.isCommunity === true ||
        (isOrganizerEvent(item) && item?.event?.[0]?.visibility !== "private")
    ) || [];
  const when = (item) => new Date(item?.event?.[0]?.occurs_at || 0).getTime() || Infinity;
  return [...list].sort((a, b) => when(a) - when(b));
}

/**
 * @returns {object|null} shape used by discover section cards + navigation
 */
export function mapFeaturedWrapperToHotCard(wrapper) {
  const ev = getInnerEventFromWrapper(wrapper);
  const rawId = ev?.id ?? ev?._id;
  if (rawId == null || rawId === "") return null;
  const img = eventImageUrl(wrapper, ev);
  const dateRaw = ev.occurs_at_local || ev.occurs_at || ev.endDate;
  const eventPayload = Array.isArray(wrapper?.event) ? wrapper.event : ev ? [ev] : [];
  return {
    id: `api-hot-${String(rawId)}`,
    eventId: rawId,
    title: ev.name || "Event",
    date: dateRaw ? moment(dateRaw).format("MMM D, YYYY") : "",
    venue: venueLabel(ev),
    price: resolveListingPrice(ev, wrapper),
    image: img || "https://images.unsplash.com/photo-1470229722913-7c0e2dbbafd3?w=1080&q=80",
    badge: "Featured",
    badgeColor: "bg-[#E52324]",
    category: ev.domain?.name || ev.event_category?.name || "Events",
    linkTo: `/tickets/group.details/${rawId}`,
    linkState: {
      data: eventPayload,
      image: wrapper.image ? `${ApiMediaURL}/${wrapper.image}` : "",
    },
    _fromApi: true,
  };
}

export function mapFeaturedWrapperToTrendingCard(wrapper) {
  const ev = getInnerEventFromWrapper(wrapper);
  const rawId = ev?.id ?? ev?._id;
  if (rawId == null || rawId === "") return null;
  const img = eventImageUrl(wrapper, ev);
  const dateRaw = ev.occurs_at_local || ev.occurs_at || ev.endDate;
  const eventPayload = Array.isArray(wrapper?.event) ? wrapper.event : ev ? [ev] : [];
  return {
    id: `api-trend-${String(rawId)}`,
    eventId: rawId,
    title: ev.name || "Event",
    date: dateRaw ? moment(dateRaw).format("MMM D, YYYY") : "",
    venue: venueLabel(ev),
    price: resolveListingPrice(ev, wrapper),
    image: img || "https://images.unsplash.com/photo-1501281667655-bf7f34cbdb67?w=1080&q=80",
    badge: "Trending",
    badgeColor: "bg-[#E52324]",
    linkTo: `/tickets/group.details/${rawId}`,
    linkState: {
      data: eventPayload,
      image: wrapper.image ? `${ApiMediaURL}/${wrapper.image}` : "",
    },
    _fromApi: true,
  };
}

export function mapCommunityWrapperToCard(wrapper) {
  const ev = wrapper?.event?.[0] || wrapper;
  const id = wrapper?._id || ev?._id || ev?.id;
  if (!id) return null;
  const img = eventImageUrl(wrapper, ev);
  const price = resolveListingPrice(ev, wrapper);
  return {
    id: `api-comm-${id}`,
    eventId: id,
    title: ev.name || "Community Event",
    date: ev.occurs_at_local ? moment(ev.occurs_at_local).format("MMM D, YYYY") : "",
    venue: venueLabel(ev),
    price,
    image: img || "https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=800&q=80",
    badge: "Community",
    badgeColor: "bg-emerald-500",
    linkTo: `/tickets/group.details/${id}`,
    linkState: {
      data: Array.isArray(wrapper.event) ? wrapper.event : [ev],
      image: wrapper.image ? `${ApiMediaURL}/${wrapper.image}` : "",
    },
    shareItem: {
      id,
      _id: id,
      name: ev.name || "Community Event",
      venue: ev.venue,
      occurs_at_local: ev.occurs_at_local || ev.occurs_at,
      _isUserCreated: true,
    },
    _fromApi: true,
  };
}
